import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { pricingPlans, pricingIncludes } from '../../data/pricing';

const Section = styled.section`
  position: relative;
  padding: clamp(88px, 11vw, 120px) 0;
  overflow: hidden;
  background: radial-gradient(ellipse 60% 40% at 50% 100%, rgba(14, 107, 60, 0.06) 0%, transparent 55%),
    linear-gradient(180deg, #f8fafb 0%, #fff 50%, #f0faf4 100%);
`;

const Bg = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;

  .grid {
    position: absolute;
    inset: 0;
    background-image: linear-gradient(rgba(14, 107, 60, 0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(14, 107, 60, 0.03) 1px, transparent 1px);
    background-size: 48px 48px;
    mask-image: radial-gradient(ellipse 70% 50% at 50% 50%, black 0%, transparent 75%);
  }
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 var(--container-padding, 24px);
`;

const Header = styled(motion.header)`
  text-align: center;
  max-width: 620px;
  margin: 0 auto clamp(40px, 6vw, 52px);
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  margin-bottom: 18px;
  border-radius: 100px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(240, 250, 244, 0.95));
  border: 1px solid rgba(14, 107, 60, 0.14);
  box-shadow: 0 4px 20px rgba(14, 107, 60, 0.08);
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: #0e6b3c;

  i {
    color: #6dbb1a;
  }
`;

const Title = styled.h2`
  font-family: var(--font-display);
  font-size: clamp(2rem, 4.8vw, 3rem);
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1.12;
  color: #07111f;
  margin: 0 0 14px;

  span {
    background: linear-gradient(135deg, #0e6b3c 0%, #6dbb1a 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const Sub = styled.p`
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.75;
  color: #64748b;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
  align-items: stretch;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    max-width: 420px;
    margin: 0 auto;
  }
`;

const CardWrap = styled(motion.article)`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 24px;
  padding: ${(p) => (p.$featured ? '2px' : '1px')};
  background: ${(p) =>
    p.$featured
      ? 'linear-gradient(145deg, #0e6b3c, #6dbb1a, #f2b705)'
      : 'linear-gradient(145deg, rgba(14, 107, 60, 0.2), rgba(14, 107, 60, 0.05))'};
  box-shadow: ${(p) =>
    p.$featured ? '0 28px 64px rgba(14, 107, 60, 0.28)' : '0 8px 32px rgba(15, 23, 42, 0.06)'};
  transform: ${(p) => (p.$featured ? 'scale(1.03)' : 'none')};
  z-index: ${(p) => (p.$featured ? 2 : 1)};

  @media (max-width: 968px) {
    transform: none;
  }
`;

const PopularRibbon = styled.div`
  position: absolute;
  top: -1px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  padding: 8px 20px;
  border-radius: 0 0 14px 14px;
  background: linear-gradient(135deg, #f2b705, #fbbf24);
  color: #07111f;
  font-size: 0.68rem;
  font-weight: 800;
  font-family: var(--font-display);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  box-shadow: 0 8px 20px rgba(242, 183, 5, 0.35);
`;

const Card = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  border-radius: ${(p) => (p.$featured ? '22px' : '23px')};
  background: ${(p) => (p.$featured ? 'linear-gradient(165deg, #0a5a32 0%, #0e6b3c 45%, #1e3a5f 100%)' : '#fff')};
  padding: clamp(28px, 4vw, 36px) clamp(22px, 3vw, 28px) clamp(24px, 3vw, 28px);
  overflow: hidden;
  position: relative;

  ${(p) =>
    p.$featured &&
    `
    &::before {
      content: '';
      position: absolute;
      top: -30%;
      right: -20%;
      width: 200px;
      height: 200px;
      border-radius: 50%;
      background: rgba(109, 187, 26, 0.2);
      pointer-events: none;
    }
  `}
`;

const PlanIcon = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
  font-size: 1.25rem;
  background: ${(p) =>
    p.$featured ? 'rgba(255, 255, 255, 0.15)' : 'linear-gradient(135deg, rgba(14, 107, 60, 0.1), rgba(109, 187, 26, 0.12))'};
  border: 1px solid ${(p) => (p.$featured ? 'rgba(255,255,255,0.2)' : 'rgba(14, 107, 60, 0.15)')};
  color: ${(p) => (p.$featured ? '#fff' : '#0e6b3c')};
`;

const Tagline = styled.span`
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${(p) => (p.$featured ? 'rgba(255,255,255,0.65)' : '#94a3b8')};
  margin-bottom: 6px;
`;

const PlanName = styled.h3`
  font-family: var(--font-display);
  font-size: 1.35rem;
  font-weight: 800;
  color: ${(p) => (p.$featured ? '#fff' : '#07111f')};
  margin: 0 0 16px;
  line-height: 1.2;
`;

const PriceBlock = styled.div`
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid ${(p) => (p.$featured ? 'rgba(255,255,255,0.12)' : '#eef2f6')};

  .amount {
    font-family: var(--font-display);
    font-size: clamp(2.25rem, 4vw, 2.75rem);
    font-weight: 800;
    line-height: 1;
    color: ${(p) => (p.$featured ? '#fff' : '#0e6b3c')};
    letter-spacing: -0.03em;
  }

  .period {
    display: block;
    margin-top: 6px;
    font-size: 0.85rem;
    font-weight: 500;
    color: ${(p) => (p.$featured ? 'rgba(255,255,255,0.7)' : '#94a3b8')};
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0 0 28px;
  padding: 0;
  flex: 1;

  li {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 10px 0;
    font-size: 0.88rem;
    line-height: 1.45;
    color: ${(p) => (p.$featured ? 'rgba(255,255,255,0.92)' : '#475569')};

    &:not(:last-child) {
      border-bottom: 1px solid ${(p) => (p.$featured ? 'rgba(255,255,255,0.08)' : '#f8fafc')};
    }
  }
`;

const Check = styled.span`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.55rem;
  background: ${(p) => (p.$featured ? 'rgba(255,255,255,0.2)' : 'rgba(14, 107, 60, 0.1)')};
  color: ${(p) => (p.$featured ? '#f2b705' : '#0e6b3c')};
`;

const Btn = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 15px 24px;
  border-radius: 14px;
  font-weight: 700;
  font-size: 0.9rem;
  font-family: var(--font-display);
  text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s;
  background: ${(p) => (p.$featured ? '#fff' : 'linear-gradient(135deg, #0e6b3c, #0a5a32)')};
  color: ${(p) => (p.$featured ? '#0e6b3c' : '#fff')};
  box-shadow: ${(p) =>
    p.$featured ? '0 8px 24px rgba(0,0,0,0.15)' : '0 10px 28px rgba(14, 107, 60, 0.28)'};

  &:hover {
    transform: translateY(-2px);
  }
`;

const Includes = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-top: clamp(40px, 6vw, 52px);
  padding-top: clamp(32px, 5vw, 40px);
  border-top: 1px solid #e8eef3;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 400px) {
    grid-template-columns: 1fr;
  }
`;

const IncludeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid #e8eef3;

  i {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(14, 107, 60, 0.08);
    color: #0e6b3c;
    font-size: 0.85rem;
    flex-shrink: 0;
  }

  span {
    font-size: 0.8rem;
    font-weight: 600;
    color: #334155;
    line-height: 1.3;
  }
`;

const FooterNote = styled(motion.p)`
  text-align: center;
  margin: 28px 0 0;
  font-size: 0.88rem;
  color: #64748b;

  a {
    color: #0e6b3c;
    font-weight: 700;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const HomePricing = ({ showHeader = true, id = 'pricing' }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.08 });

  return (
    <Section ref={ref} id={id}>
      <Bg>
        <div className="grid" />
      </Bg>

      <Inner>
        {showHeader && (
          <Header
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.55 }}
          >
            <Badge>
              <i className="fas fa-tags" />
              Pricing plans
            </Badge>
            <Title>
              Affordable <span>Pricing</span>
            </Title>
            <Sub>
              Transparent INR packages for startups and businesses — or a custom quote for CRM,
              ERP, and enterprise builds.
            </Sub>
          </Header>
        )}

        <Grid>
          {pricingPlans.map((plan, i) => (
            <CardWrap
              key={plan.id}
              $featured={plan.featured}
              initial={{ opacity: 0, y: 32 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.1, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            >
              {plan.featured && <PopularRibbon>Most popular</PopularRibbon>}
              <Card $featured={plan.featured}>
                <PlanIcon $featured={plan.featured}>
                  <i className={plan.icon} />
                </PlanIcon>
                <Tagline $featured={plan.featured}>{plan.tagline}</Tagline>
                <PlanName $featured={plan.featured}>{plan.name}</PlanName>
                <PriceBlock $featured={plan.featured}>
                  <span className="amount">{plan.price}</span>
                  {plan.period ? <span className="period">{plan.period}</span> : null}
                </PriceBlock>
                <List $featured={plan.featured}>
                  {plan.features.map((f) => (
                    <li key={f}>
                      <Check $featured={plan.featured}>
                        <i className="fas fa-check" />
                      </Check>
                      {f}
                    </li>
                  ))}
                </List>
                <Btn to="/contact" $featured={plan.featured}>
                  {plan.price === 'Custom' ? 'Request quote' : 'Get started'}
                  <i className="fas fa-arrow-right" />
                </Btn>
              </Card>
            </CardWrap>
          ))}
        </Grid>

        <Includes
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.35 }}
        >
          {pricingIncludes.map((item) => (
            <IncludeItem key={item.label}>
              <i className={item.icon} />
              <span>{item.label}</span>
            </IncludeItem>
          ))}
        </Includes>

        <FooterNote
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.45 }}
        >
          Need LMS, POS, or a full product build?{' '}
          <Link to="/contact">Contact us</Link> for a tailored proposal within 24 hours.
        </FooterNote>
      </Inner>
    </Section>
  );
};

export default HomePricing;
